import type { ChapterItem } from '@app/_proto/Protos/chapters';
import type { NovelItem } from '@app/_proto/Protos/novels';
import Divider from '@app/components/Divider';
import { useChapterSettings } from '@app/components/chapters/hooks';
import { useTheme } from '@app/components/hooks';
import AdvanceChapterBar from '@app/domains/chapter/components/AdvanceChapterBar';
import ChapterContentTitle from '@app/domains/chapter/components/ChapterContentTitle';
import ChapterTopNavigation from '@app/domains/chapter/components/ChapterTopNavigation';
import { checkHavingSneakPeekAccess } from '@app/domains/chapter/utils';
import { ZINDEX_USAGES } from '@app/domains/common/constants/zindex';
import useChapterLockStatus from '@app/domains/common/hooks/useChapterLockStatus';
import useChapterStatus from '@app/domains/common/hooks/useChapterStatus';
import { useAuth } from '@app/libs/auth';

type Props = {
    novel: NovelItem;
    chapter?: ChapterItem | undefined;
};

export default function ChapterHeader({ novel, chapter }: Props) {
    const { user } = useAuth();
    const { isAdvancedChapter } = useChapterStatus({ chapter });
    const { isLockedBySneakPeekChapter } = useChapterLockStatus({ novel, chapter });
    const [settings] = useChapterSettings();
    const { name: themeName } = useTheme();

    return (
        <>
            {novel && (
                <div className="space-y-[12px] pb-[12px]">
                    <ChapterTopNavigation novel={novel} chapter={chapter} />
                </div>
            )}
            <Divider isFullScreen className="lg:w-[1024px]" />
            <div
                className="relative mx-auto max-w-[840px]"
                style={{ color: settings.viewerContrast[themeName].color, zIndex: ZINDEX_USAGES.CHAPTER_HEADER }}
            >
                {(!isLockedBySneakPeekChapter ||
                    checkHavingSneakPeekAccess({ user: user ?? undefined, novelId: novel.id })) && (
                    <div className="mb-12">
                        {isAdvancedChapter && (
                            <div className="-mx-20 sm:mx-0 sm:mt-20">
                                <AdvanceChapterBar />
                            </div>
                        )}
                        <div className="mt-20">
                            <ChapterContentTitle novel={novel} chapter={chapter} />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
